import { z } from 'zod';

import { isAiCapability, type AiCapability } from '../ai-capability/ai-capability';
import { type AiTokenUsage } from '../ai-token-usage';

/**
 * What one model costs, per million tokens, in USD.
 *
 * Four rates because vendors bill four classes (see `AiTokenUsage`). An
 * embedding model prices input only, so `outputPerMillionUsd: 0` is a legitimate
 * value rather than a missing one.
 */
export const AiModelPricingSchema = z
  .object({
    inputPerMillionUsd: z.number().min(0),
    outputPerMillionUsd: z.number().min(0),
    /** Prompt-cache reads — typically ~0.1x the input rate. */
    cachedInputPerMillionUsd: z.number().min(0),
    /** Prompt-cache writes — typically 1.25–2x the input rate. */
    cacheWritePerMillionUsd: z.number().min(0),
  })
  .strict();

export type AiModelPricing = z.infer<typeof AiModelPricingSchema>;

/** The whole `ai.pricing` setting: a rate table keyed by {@link aiPricingKey}. */
export const AiPricingTableSchema = z.record(z.string(), AiModelPricingSchema);

export type AiPricingTable = z.infer<typeof AiPricingTableSchema>;

/**
 * The rate-table key for a resolved provider+model.
 *
 * The capability is part of it because one provider serves both: Gemini's text
 * and embedding models price an order of magnitude apart, and a table keyed on
 * provider+model alone would silently reuse whichever entry was written first.
 */
export function aiPricingKey(capability: AiCapability, provider: string, model: string): string {
  return `${capability}:${provider}:${model}`;
}

/** A rate key taken apart — what the row a rate table draws is about. */
export interface AiPricingKeyParts {
  capability: AiCapability;
  provider: string;
  model: string;
}

/**
 * The inverse of {@link aiPricingKey}, or `null` when the string is not one.
 *
 * It lives beside the writer so the two cannot drift, and it is **strict on
 * purpose**: a rate table drawn from stored keys shows a row per parse, so a
 * lenient reader would invent a provider called `ai-text` out of the fallback
 * key `default:ai-text`, and a row for a capability the platform does not serve
 * out of a stale one. `null` means "not a model rate" — which is a real answer
 * the caller has to handle, not a failure.
 *
 * The model takes everything after the second separator, so a vendor id that
 * contains a `:` survives being read back.
 */
export function parseAiPricingKey(key: string): AiPricingKeyParts | null {
  const [capability, provider, ...rest] = key.split(':');
  const model = rest.join(':');

  if (capability === undefined || provider === undefined) return null;
  if (provider === '' || model === '') return null;
  if (!isAiCapability(capability)) return null;

  return { capability, provider, model };
}

/**
 * The key of the fallback rate used when a configured model has no entry.
 *
 * Per capability, and deliberately **expensive** in the coded defaults: a budget
 * guard that under-estimates an unknown model is a budget guard that does not
 * exist, so an unpriced model is over-charged against the cap (and reported, so
 * somebody fixes the table) rather than waved through.
 */
export function aiPricingFallbackKey(capability: AiCapability): string {
  return `${FALLBACK_PREFIX}:${capability}`;
}

/** The head of a fallback key — never a capability, so the two cannot collide. */
const FALLBACK_PREFIX = 'default';

/**
 * The capability a fallback key belongs to, or `null` when it is not one.
 *
 * A rate table needs both halves of this question — "is this row the fallback"
 * and "whose" — and answering them from one parse keeps a row that says
 * *ai-text* from being filed under *ai-embedding*.
 */
export function aiPricingFallbackCapability(key: string): AiCapability | null {
  const [prefix, capability, ...rest] = key.split(':');

  if (prefix !== FALLBACK_PREFIX || capability === undefined || rest.length > 0) return null;

  return isAiCapability(capability) ? capability : null;
}

export function isAiPricingFallbackKey(key: string): boolean {
  return aiPricingFallbackCapability(key) !== null;
}

/**
 * The USD cost of one call's token usage.
 *
 * Pure arithmetic over the four classes — no rounding, because the budget guard
 * accumulates thousands of these and rounding each one to cents drifts.
 */
export function aiCostUsd(usage: AiTokenUsage, pricing: AiModelPricing): number {
  const perToken =
    usage.inputTokens * pricing.inputPerMillionUsd +
    usage.outputTokens * pricing.outputPerMillionUsd +
    usage.cachedInputTokens * pricing.cachedInputPerMillionUsd +
    usage.cacheWriteTokens * pricing.cacheWritePerMillionUsd;

  return perToken / 1_000_000;
}
